import { useState, useRef, useEffect, ReactNode } from 'react';

export interface DebugWrapperProps {
  children: ReactNode;
  sequence?: string;
}

/**
 * Only renders children when the specified key sequence is pressed.
 * The default sequence is 'DEBUG', which is activated by holding the space key and typing 'D', 'E', 'B', 'U', 'G'.
 */
export function DebugWrapper({ children, sequence = 'DEBUG' }: DebugWrapperProps) {
  const [visible, setVisible] = useState(false);
  const buffer = useRef('');
  const spaceHeld = useRef(false);

  sequence = sequence.toUpperCase();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeld.current = true;
        buffer.current = '';
      } else if (spaceHeld.current && /^[a-zA-Z]$/.test(e.key)) {
        buffer.current += e.key.toUpperCase();
        if (buffer.current === sequence) {
          setVisible((v) => !v);
          buffer.current = '';
        } else if (!sequence.startsWith(buffer.current)) {
          buffer.current = '';
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeld.current = false;
        buffer.current = '';
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [sequence]);

  return visible ? <>{children}</> : null;
}
